import { createFileRoute, redirect } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RootLayout } from '@/components/layout/RootLayout'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassButton } from '@/components/ui/GlassButton'
import { invoicesQueryOptions } from '@/lib/queryOptions'
import { api } from '@/lib/api'
import { useT } from '@/i18n/provider'
import { formatCompactNumber, formatCurrencyUsd } from '@/lib/format'
import { useLocale } from '@/i18n/provider'

function defaultDateRange() {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - 30)
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  }
}

export const Route = createFileRoute('/dashboard/billing')({
  beforeLoad: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(invoicesQueryOptions()).catch(() => {
      throw redirect({ to: '/auth/login' })
    })
  },
  component: BillingPage,
})

function BillingPage() {
  const t = useT()
  const { locale } = useLocale()
  const qc = useQueryClient()
  const { data: invoices = [] } = useQuery(invoicesQueryOptions())
  const initialRange = useMemo(() => defaultDateRange(), [])
  const [startDate, setStartDate] = useState(initialRange.start)
  const [endDate, setEndDate] = useState(initialRange.end)
  const [revenueSharePct, setRevenueSharePct] = useState('15')

  const generateMutation = useMutation({
    mutationFn: api.billing.invoices.generate,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing', 'invoices'] }),
  })

  return (
    <RootLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">{t('billing.title')}</h1>
          <p className="text-sm text-white/40 mt-1">{t('billing.subtitle')}</p>
        </div>

        <GlassCard className="space-y-4">
          <h2 className="text-sm font-semibold text-white/75">{t('billing.generateTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="space-y-1">
              <span className="text-xs text-white/60">{t('billing.periodStart')}</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="glass w-full px-3 py-2 text-sm text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-white/60">{t('billing.periodEnd')}</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="glass w-full px-3 py-2 text-sm text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-white/60">{t('billing.revenueSharePct')}</span>
              <input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={revenueSharePct}
                onChange={(e) => setRevenueSharePct(e.target.value)}
                className="glass w-full px-3 py-2 text-sm text-white bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              />
            </label>
          </div>
          <GlassButton
            onClick={() => generateMutation.mutate({
              periodStart: new Date(`${startDate}T00:00:00.000Z`).toISOString(),
              periodEnd: new Date(`${endDate}T00:00:00.000Z`).toISOString(),
              revenueSharePct: Number(revenueSharePct),
            })}
            loading={generateMutation.isPending}
          >
            {t('billing.generateButton')}
          </GlassButton>
        </GlassCard>

        {invoices.length === 0 ? (
          <GlassCard className="text-center py-10">
            <p className="text-white/40 text-sm">{t('billing.noInvoices')}</p>
          </GlassCard>
        ) : (
          <div className="space-y-3">
            {invoices.map((invoice) => (
              <GlassCard key={invoice.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-white text-sm">
                    {invoice.periodStart.slice(0, 10)} → {invoice.periodEnd.slice(0, 10)}
                  </p>
                  <p className="text-xs text-white/40">{invoice.status}</p>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <p className="text-xs text-white/40">{t('billing.totalTokens')}</p>
                    <p className="text-sm text-white">{formatCompactNumber(invoice.totalTokens, locale)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/40">{t('billing.totalRequests')}</p>
                    <p className="text-sm text-white">{formatCompactNumber(invoice.totalRequests, locale)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/40">{t('billing.totalCost')}</p>
                    <p className="text-sm text-white">{formatCurrencyUsd(Number(invoice.totalCostUsd), locale, 2)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-white/40">{t('billing.revenueShare')}</p>
                    <p className="text-sm text-white">{formatCurrencyUsd(Number(invoice.totalRevenueShareUsd), locale, 2)}</p>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        )}
      </div>
    </RootLayout>
  )
}
